/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import ReactDOM from "react-dom/client";
import { Provider, useDispatch } from "react-redux";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import updateLocale from "dayjs/plugin/updateLocale";

import useColorMode from "./configs/theme/hooks/use-color-mode.hook";
import store from "./store";
import Router from "./router";
import { ColorModeContext } from "./configs/theme/context/color-mode.context";
import Sidebar from "./components/layouts/sidebar";
import useSidebarToggle from "./components/layouts/sidebar/hooks/use-sidebar-toggle.hook";
import { SidebarContext } from "./components/layouts/sidebar/context/sidebar.context";
import useHeaderToggle from "./components/layouts/header/hooks/use-header-toggle.hook";
import { HeaderContext } from "./components/layouts/header/context/header.context";
import SuccessSnackbar from "./components/ui/snackbar";

import "./index.css";
import Header from "./components/layouts/header";
import useAuthorized from "./hooks/use-authorized.hook";
import { AuthApi } from "./services/api/auth";
import { profileData, signInSuccess } from "./store/actions/auth";
import { AuthStorage } from "./services/storage/auth.storage";
import { purgeStaleSessionState } from "./services/storage/session-state.storage";
import { ensureAccessToken, forceLogout } from "./services/api/interceptors";
import { SubscriptionProvider } from "./contexts/subscription.context";
import RouteEnum from "./router/enums/route.enum";
import { isInviteAcceptanceUrl } from "./utils/invitation";
import { AUTH_PATHS } from "./configs/constants";
import useIdleTimeout from "./hooks/use-idle-timeout.hook";

import "./i18n";
import { ToastProvider } from "./components/ui/toast";
import PaymentSuccessPopup from "./components/pages/uploading-files/success-popup/PaymentSuccessPopup";
import Footer from "./components/layouts/footer";

dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  weekStart: 1,
});

// isInviteAcceptanceUrl is imported from utils/invitation — single authoritative implementation

// Helper to check if current path is an auth path that shouldn't show sidebar/header
export const isAuthPage = (pathname) => AUTH_PATHS.includes(pathname);

export const App = () => {
  const { colorMode, currentTheme } = useColorMode();
  const sidebarToggle = useSidebarToggle();
  const headerToggle = useHeaderToggle();
  const isAuth = useAuthorized();
  const dispatch = useDispatch();
  const [hasInviteParams, setHasInviteParams] = useState(false);
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const navigate = useNavigate();
  const sidebarToggleRef = useRef(sidebarToggle);
  sidebarToggleRef.current = sidebarToggle;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const [open, setOpen] = useState(false);

  // Dispatch logout after 30 minutes of inactivity for authenticated sessions.
  useIdleTimeout(30 * 60 * 1000, isAuth);

  // Check if this is an invite URL by path or parameters
  const isInvitePage =
    location.pathname === RouteEnum.ACCEPT_INVITE ||
    location.pathname.startsWith("/accept-invite/") ||
    isInviteAcceptanceUrl();

  const showHeaderSidebar = !isAuthPage(location.pathname) && !isInvitePage;
  const showMainSidebar = showHeaderSidebar;

  // Check for invite parameters
  useEffect(() => {
    const hasInvite = isInviteAcceptanceUrl();
    setHasInviteParams(hasInvite);
  }, [location]);

  // Handle authentication
  useEffect(() => {
    // Don't attempt to load user data if we're on an invite page
    if (
      hasInviteParams ||
      window.location.pathname === RouteEnum.ACCEPT_INVITE ||
      window.location.pathname.startsWith("/accept-invite/")
    ) {
      return;
    }

    if (isAuth) {
      sidebarToggleRef.current.toggle(true);
      (async () => {
        try {
          // The access token is memory-only, so after a reload there is none.
          // Recover it from the httpOnly refresh cookie before any authenticated
          // request goes out; without this every call below 401s.
          const accessToken = await ensureAccessToken();
          if (!accessToken) {
            forceLogout();
            return;
          }
          // Check onboarding status first; /profile/ returns 403 for users
          // without a CompanyUserRole and would crash the bootstrap otherwise.
          const onboarding = await AuthApi.getOnboardingStatus();
          if (onboarding?.status === "needs_company") {
            const onboardingUser = {
              email: onboarding.email || "",
              first_name: onboarding.first_name || "",
              last_name: onboarding.last_name || "",
              id: "pending-onboarding",
              role: null,
              company: null,
              requires_onboarding: true,
            };
            dispatch(signInSuccess(onboardingUser));
            AuthStorage.storeUser(onboardingUser);
            // If the user lands somewhere else while not yet onboarded, send them
            // to the onboarding page (skip if they're already there to avoid loops).
            if (window.location.pathname !== RouteEnum.ONBOARDING_CREATE_COMPANY) {
              navigateRef.current(RouteEnum.ONBOARDING_CREATE_COMPANY, { replace: true });
            }
            return;
          }
          if (onboarding?.status === "pending_company_approval") {
            // Company created but not yet approved by staff. Don't call /profile/
            // (it 403s while pending); lock the user to the pending-approval page.
            const pendingUser = {
              email: onboarding.email || "",
              first_name: onboarding.first_name || "",
              last_name: onboarding.last_name || "",
              id: "pending-approval",
              role: "admin",
              company: null,
              requires_approval: true,
            };
            dispatch(signInSuccess(pendingUser));
            AuthStorage.storeUser(pendingUser);
            if (window.location.pathname !== RouteEnum.ONBOARDING_PENDING_APPROVAL) {
              navigateRef.current(RouteEnum.ONBOARDING_PENDING_APPROVAL, { replace: true });
            }
            return;
          }
          const authData = await AuthApi.getAuthUser();
          dispatch(signInSuccess(authData));
          dispatch(profileData(authData));
          AuthStorage.storeUser(authData);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error("Error fetching user data:", error);
          }
        }
      })();
    }
  }, [isAuth, hasInviteParams, dispatch]);

  useEffect(() => {
    if (queryParams.get("payment") === "success") {
      setOpen(true);
    }
  }, [queryParams]);

  const handleClose = () => {
    setOpen(false);
    //clear params
    navigate(location.pathname, { replace: true });
  };

  return (
    <>
      <ColorModeContext.Provider value={colorMode}>
        <SidebarContext.Provider value={sidebarToggle}>
          <HeaderContext.Provider value={headerToggle}>
            <ThemeProvider theme={currentTheme}>
              <CssBaseline />
              <SubscriptionProvider>
                <SuccessSnackbar />
                <ToastProvider>
                  <Box
                    sx={{ display: "flex" }}
                    className={currentTheme.palette.mode}
                  >
                    {showHeaderSidebar && headerToggle.visible && <Header />}
                    {showMainSidebar && <Sidebar />}
                    <Router />
                  </Box>
                  <Footer />
                </ToastProvider>
              </SubscriptionProvider>
            </ThemeProvider>
          </HeaderContext.Provider>
        </SidebarContext.Provider>
      </ColorModeContext.Provider>
      <PaymentSuccessPopup open={open} closeHandler={handleClose} />
    </>
  );
};

window.addEventListener("unhandledrejection", (event) => {
  if (event.reason?.name === "ChunkLoadError") {
    const key = "chunk_reload_attempted";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.location.reload();
    }
  }
});

// Must run before React mounts: the parsed-files table reads its lock state
// from localStorage in a mount effect, so a purge afterwards would be a no-op
// for the current page load.
purgeStaleSessionState();

const rootElement = document.getElementById("root");

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <Provider store={store}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <App />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
